import type { TrialInfo } from './getTrialInfoFromLiveCall';

type TrialRenderConfig = {
  warningDialog: {
    text: string;
    title: string;
  };
  membershipExpired: {
    text: string;
    title: string;
  };
  registration: {
    text: string;
  };
  pricingBox: {
    text: string;
  };
  days?: number | null;
  isAnnual: boolean;
  isMonthly: boolean;
  planKinds: string[];
  isEligible?: boolean | null;
  isCurrent?: boolean | null;
  isGourmet14Day?: boolean;
  isFacebook30Day?: boolean;
  isRides7Day?: boolean;
  isRides3Day?: boolean;
  isOnline7Day?: boolean;
  isTaskrabbit14Day?: boolean;
  isRides3DayAnnual?: boolean;
  isGourmet7DayAnnual?: boolean;
  isGrocery7DayAnnual?: boolean;
  isGourmet7Day?: boolean;
  isScan30Day?: boolean;
};

export const getTrialRenderConfig = (trialInfo: null | TrialInfo): null | TrialRenderConfig => {
  if (!trialInfo) {
    return null;
  }

  switch (trialInfo.periodCode) {
    case 'scanFree30DaysTrial':
      return {
        ...trialInfo,
        isScan30Day: true,
        planKinds: ['value'],
        isAnnual: false,
        isMonthly: true,
        warningDialog: {
          text: 'Offer the 30-day trial to this customer for Value plan. They would also get 2 free rides up to 5 miles each!',
          title: 'Reminder: 30-day SCAN trial and 2 free rides',
        },
        membershipExpired: {
          text: 'This customer is soft-registered for the 30-day trial before their membership starts. Please follow instructions to complete registration.',
          title: 'Reminder: 30-day SCAN trial',
        },
        registration: {
          text: 'This client will start membership for free for 30 days and start paid membership after 30 days.',
        },
        pricingBox: {
          text: '30 Days Trial: Value Monthly ONLY',
        },
      };
    case 'facebookFirstMonthTrialPlusShipping':
      return {
        ...trialInfo,
        isFacebook30Day: true,
        planKinds: ['basic', 'value', 'premium', 'totalCare', 'basicAnnual', 'valueAnnual', 'premiumAnnual', 'totalCareAnnual'],
        isAnnual: true,
        isMonthly: true,
        warningDialog: {
          text: 'Offer the 30-day trial to this customer for selected plans. They are in the test group.',
          title: 'Reminder: 30-day Facebook trial',
        },
        membershipExpired: {
          text: 'This customer is soft-registered for the 30-day trial before their membership starts. Please follow instructions to complete registration.',
          title: 'Reminder: 30-day Facebook trial',
        },
        registration: {
          text: 'This client will start membership for free for 30 days and start paid membership after 30 days.',
        },
        pricingBox: {
          text: '30-day Facebook Trial',
        },
      };
    case 'gourmet14DaysTrial':
      return {
        ...trialInfo,
        isGourmet14Day: true,
        planKinds: ['basic', 'value', 'premium', 'totalCare', 'basicAnnual', 'valueAnnual', 'premiumAnnual', 'totalCareAnnual'],
        isAnnual: true,
        isMonthly: true,
        warningDialog: {
          text: 'Offer the 14-day trial to this customer. They are in the test group.',
          title: 'Reminder: 14-day trial',
        },
        membershipExpired: {
          text: 'This customer is soft-registered for the 14-day trial before their membership starts. Please follow instructions to complete registration.',
          title: 'Reminder: 14-day trial',
        },
        registration: {
          text: 'This client will start membership for free for 14 days and start paid membership after 14 days.',
        },
        pricingBox: {
          text: '14-day Gourmet Trial',
        },
      };
    case 'taskRabbit14DayTrial':
      return {
        ...trialInfo,
        isTaskrabbit14Day: true,
        planKinds: ['basic', 'value', 'premium', 'totalCare', 'basicAnnual', 'valueAnnual', 'premiumAnnual', 'totalCareAnnual'],
        isAnnual: true,
        isMonthly: true,
        warningDialog: {
          text: 'Offer the 14-day trial to this customer. They are eligible for Taskrabbit Home Services Trial.',
          title: 'Reminder: 14-day Taskrabbit Home Services Trial',
        },
        membershipExpired: {
          text: 'This customer is soft-registered for the 14-day Taskrabbit Home Services Trial before their membership starts. Please follow instructions to complete registration.',
          title: 'Reminder: 14-day Taskrabbit Home Services Trial',
        },
        registration: {
          text: 'This client will start membership for free for 14 days and start paid membership after 14 days.',
        },
        pricingBox: {
          text: '14-day Taskrabbit Home Services Trial',
        },
      };
    case 'online7DayTrial':
      return {
        ...trialInfo,
        isOnline7Day: true,
        planKinds: ['valueAnnual'],
        isAnnual: true,
        isMonthly: false,
        warningDialog: {
          text: 'Offer the 7-day trial to this customer for Value Annual plan. They are in the test group.',
          title: 'Reminder: 7-day trial (Value Annual plan)',
        },
        membershipExpired: {
          text: 'This customer is soft-registered for the 7-day trial (Value Annual plan) before their membership starts. Please follow instructions to complete registration.',
          title: 'Reminder: 7-day trial (Value Annual plan)',
        },
        registration: {
          text: 'This client will start membership for free for 7 days and start paid membership after 7 days on Value Annual plan.',
        },
        pricingBox: {
          text: '7-day Trial (Value Annual plan)',
        },
      };
    case 'rides7DayTrial':
      return {
        ...trialInfo,
        isRides7Day: true,
        planKinds: ['valueAnnual', 'basicAnnual'],
        isAnnual: true,
        isMonthly: false,
        warningDialog: {
          text: 'Offer the 7-day trial to this customer for selected Annual plans. They are in the test group.',
          title: 'Reminder: 7-day trial',
        },
        membershipExpired: {
          text: 'This customer is soft-registered for the 7-day trial before their membership starts. Please follow instructions to complete registration.',
          title: 'Reminder: 7-day trial',
        },
        registration: {
          text: 'This client will start membership for free for 7 days and start paid membership after 7 days on selected Annual plans.',
        },
        pricingBox: {
          text: '7-day Trial (Annual Plan)',
        },
      };
    case 'rides3DayTrial':
      return {
        ...trialInfo,
        isRides3Day: true,
        planKinds: ['value', 'basic'],
        isAnnual: false,
        isMonthly: true,
        warningDialog: {
          text: 'Offer the 3-day trial to this customer for selected Monthly plans. They are in the test group.',
          title: 'Reminder: 3-day trial',
        },
        membershipExpired: {
          text: 'This customer is soft-registered for the 3-day trial before their membership starts. Please follow instructions to complete registration.',
          title: 'Reminder: 3-day trial',
        },
        registration: {
          text: 'This client will start membership for free for 3 days and start paid membership after 3 days on selected Monthly plans.',
        },
        pricingBox: {
          text: '3-day Trial',
        },
      };
    case 'rides3DayTrialAnnual':
      return {
        ...trialInfo,
        isRides3DayAnnual: true,
        planKinds: ['valueAnnual', 'basicAnnual', 'fixedIncomeAnnual'],
        isAnnual: true,
        isMonthly: false,
        warningDialog: {
          text: 'Offer the 3-day trial to this customer for selected Annual plans. They are in the test group.',
          title: 'Reminder: 3-day trial',
        },
        membershipExpired: {
          text: 'This customer is soft-registered for the 3-day trial before their membership starts. Please follow instructions to complete registration.',
          title: 'Reminder: 3-day trial',
        },
        registration: {
          text: 'This client will start membership for free for 3 days and start paid membership after 3 days on selected Annual plans.',
        },
        pricingBox: {
          text: '3-day Trial (Annual Plan)',
        },
      };
    case 'gourmet7DaysTrialAnnual':
      return {
        ...trialInfo,
        isGourmet7DayAnnual: true,
        planKinds: ['valueAnnual', 'basicAnnual', 'fixedIncomeAnnual'],
        isAnnual: true,
        isMonthly: false,
        warningDialog: {
          text: 'Offer the 7-day trial to this customer. They are in the test group.',
          title: 'Reminder: 7-day Gourmet trial (Annual Plan)',
        },
        membershipExpired: {
          text: 'This customer is soft-registered for the 7-day trial before their membership starts. Please follow instructions to complete registration.',
          title: 'Reminder: 7-day Gourmet trial (Annual Plan)',
        },
        registration: {
          text: 'This client will start membership for free for 7 days and start paid membership after 7 days.',
        },
        pricingBox: {
          text: '7-day Gourmet Trial (Annual Plan)',
        },
      };
    case 'gourmet7DaysTrial':
      return {
        ...trialInfo,
        isGourmet7Day: true,
        planKinds: [
          'valueAnnual',
          'basicAnnual',
          'fixedIncomeAnnual',
          'premiumAnnual',
          'totalCareAnnual',
          'value',
          'basic',
          'fixedIncome',
          'premium',
          'totalCare',
        ],
        isAnnual: true,
        isMonthly: true,
        warningDialog: {
          text: 'Offer the 7-day trial to this customer.',
          title: 'Reminder: 7-day Gourmet trial',
        },
        membershipExpired: {
          text: 'This customer is soft-registered for the 7-day trial before their membership starts. Please follow instructions to complete registration.',
          title: 'Reminder: 7-day Gourmet trial',
        },
        registration: {
          text: 'This client will start membership for free for 7 days and start paid membership after 7 days.',
        },
        pricingBox: {
          text: '7-day Gourmet Trial',
        },
      };
    case 'grocery7DaysTrialAnnual':
      return {
        ...trialInfo,
        isGrocery7DayAnnual: true,
        planKinds: ['valueAnnual', 'basicAnnual', 'fixedIncomeAnnual'],
        isAnnual: true,
        isMonthly: false,
        warningDialog: {
          text: 'Offer the 7-day trial to this customer. They are in the test group.',
          title: 'Reminder: 7-day Grocery trial (Annual Plan)',
        },
        membershipExpired: {
          text: 'This customer is soft-registered for the 7-day trial before their membership starts. Please follow instructions to complete registration.',
          title: 'Reminder: 7-day Grocery trial (Annual Plan)',
        },
        registration: {
          text: 'This client will start membership for free for 7 days and start paid membership after 7 days.',
        },
        pricingBox: {
          text: '7-day Grocery Trial (Annual Plan)',
        },
      };
    default:
      return null;
  }
};
